import { useEffect, useCallback } from 'react';

/**
 * Keyboard shortcuts hook for Dev Workbench
 *
 * Shortcuts:
 * - Cmd/Ctrl + Enter: Run request
 * - Cmd/Ctrl + 1-5: Switch tabs
 * - ?: Toggle shortcuts help panel
 * - M: Toggle magic values panel
 * - Arrow Up/Down: Navigate primitives when sidebar focused
 *
 * @param {Object} options
 * @param {Function} options.onRun - Callback when Cmd/Ctrl+Enter is pressed
 * @param {Function} options.onTabChange - Callback with tab id (explorer, logs, quickstart, keys, webhooks)
 * @param {Function} options.onToggleHelp - Callback to toggle shortcuts panel
 * @param {Function} options.onToggleMagicValues - Callback to toggle magic values panel
 * @param {Function} options.onNavigatePrimitive - Callback with direction ('up' or 'down')
 * @param {boolean} options.enabled - Whether shortcuts are enabled (default: true)
 */
const TAB_IDS = ['explorer', 'logs', 'quickstart', 'keys', 'webhooks'];

export function useKeyboardShortcuts({
  onRun,
  onTabChange,
  onToggleHelp,
  onToggleMagicValues,
  onNavigatePrimitive,
  enabled = true,
}) {
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs/textareas
    const target = event.target;
    const isInputElement =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable;

    const isMeta = event.metaKey || event.ctrlKey;

    // Cmd/Ctrl + Enter: Run request
    if (isMeta && event.key === 'Enter') {
      event.preventDefault();
      onRun?.();
      return;
    }

    // Cmd/Ctrl + 1-5: Switch tabs
    if (isMeta && event.key >= '1' && event.key <= '5') {
      event.preventDefault();
      const tabIndex = parseInt(event.key, 10) - 1;
      const tabId = TAB_IDS[tabIndex];
      if (tabId) {
        onTabChange?.(tabId);
      }
      return;
    }

    // Don't process single-key shortcuts if in input
    if (isInputElement) return;

    // ?: Toggle shortcuts help panel
    if (event.key === '?' || (event.shiftKey && event.key === '/')) {
      event.preventDefault();
      onToggleHelp?.();
      return;
    }

    // M: Toggle magic values panel
    if (event.key === 'm' || event.key === 'M') {
      event.preventDefault();
      onToggleMagicValues?.();
      return;
    }

    // Arrow keys: Navigate primitives (when sidebar is focused)
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      // Check if we're in the sidebar context
      const sidebar = document.querySelector('.explorer-sidebar');
      if (sidebar && sidebar.contains(document.activeElement)) {
        event.preventDefault();
        onNavigatePrimitive?.(event.key === 'ArrowUp' ? 'up' : 'down');
      }
    }

    // Escape: Close modals (handled by modal components themselves)
  }, [enabled, onRun, onTabChange, onToggleHelp, onToggleMagicValues, onNavigatePrimitive]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Platform-aware modifier key label
 * @returns {string} 'Cmd' on Mac, 'Ctrl' on Windows/Linux
 */
export function getModifierKey() {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  return isMac ? 'Cmd' : 'Ctrl';
}

/**
 * Get keyboard shortcuts list for display
 * @returns {Array<{keys: string, description: string}>}
 */
export function getShortcutsList() {
  const mod = getModifierKey();
  return [
    { keys: `${mod}+Enter`, description: 'Run API request' },
    { keys: `${mod}+1`, description: 'Switch to Explorer tab' },
    { keys: `${mod}+2`, description: 'Switch to Logs tab' },
    { keys: `${mod}+3`, description: 'Switch to Quickstart tab' },
    { keys: `${mod}+4`, description: 'Switch to API Keys tab' },
    { keys: `${mod}+5`, description: 'Switch to Webhooks tab' },
    { keys: '?', description: 'Toggle keyboard shortcuts help' },
    { keys: 'M', description: 'Toggle magic values panel' },
    { keys: 'Arrow Up/Down', description: 'Navigate primitives in sidebar' },
    { keys: 'Escape', description: 'Close modal' },
  ];
}
